import { useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import type { Key } from 'chessground/types';
import { Board, type BoardMoveEvent } from '@/chess/board';
import { KidHint, TurnIndicator, UnderstandingPrompt } from '@/ui';
import { classifySuccessOutcome, type Outcome, type FailOutcome } from '@/api/outcome';
import type { DrillPosition, RepertoireDetail } from './types';
import { openingLabApi, type AttemptPayload } from './api';
import { pickUnseenTrap } from './drillPickers';

interface Props {
  repertoire: RepertoireDetail;
  /** Positions taggées `#trap` dans le PGN. */
  traps: DrillPosition[];
  onResult: (result: 'success' | 'partial' | 'fail') => void;
}

/**
 * Mode `punish` : l'adversaire joue une imprécision typique (extraite des
 * positions `#trap` du PGN — par construction, ce sont les coups d'erreur
 * dans les variations alternatives). Léon doit jouer la réfutation, c'est-à-dire
 * le coup de la mainline immédiatement après ce trap.
 *
 * V1 simplifiée : on présente la position APRÈS le coup tag #trap, c'est à
 * Léon de jouer le coup attendu (qui devient le prochain coup de la séquence).
 */
export function DrillPunish({ repertoire, traps, onResult }: Props) {
  // Fix 2026-05-17 : Set in-memory des trap-positions déjà drillées pour
  // éviter les répétitions consécutives ("ce sont souvent les mêmes
  // exercices" — bug Léon). Clé `fenBefore`.
  const seenRef = useRef<Set<string>>(new Set());
  const [position, setPosition] = useState<DrillPosition | null>(() => pickUnseenTrap(traps, seenRef.current));
  const [attempts, setAttempts] = useState(0);
  const [phase, setPhase] = useState<'playing' | 'revealed'>('playing');
  const [lastFail, setLastFail] = useState<{
    playedMove: string;
    timeSpentMs: number;
    outcomeLogged: boolean;
  } | null>(null);
  // Cf. DrillRecall : pendant un retry on n'envoie plus d'attempt au backend
  // pour ne pas gonfler success_rate ni re-créer une carte SRS déjà créée.
  const [isRetrying, setIsRetrying] = useState(false);
  const startedAtRef = useRef<number>(performance.now());

  useEffect(() => {
    startedAtRef.current = performance.now();
    setAttempts(0);
    setPhase('playing');
    setLastFail(null);
    setIsRetrying(false);
  }, [position]);

  const chess = useMemo(
    () => (position ? new Chess(position.fenBefore) : null),
    [position],
  );

  const legalMoves = useMemo(() => {
    if (!chess) return new Map<Key, Key[]>();
    const map = new Map<Key, Key[]>();
    for (const move of chess.moves({ verbose: true })) {
      const list = map.get(move.from as Key) ?? [];
      list.push(move.to as Key);
      map.set(move.from as Key, list);
    }
    return map;
  }, [chess]);

  if (!position || !chess) {
    return (
      <p className="rounded-xl border border-ink-700 bg-ink-800 p-6 text-body text-ink-300">
        Aucun piège dans ce répertoire pour le moment.
      </p>
    );
  }

  function logAttempt(
    result: AttemptPayload['result'],
    played: string | undefined,
    timeSpentMs: number,
    outcome?: Outcome,
  ) {
    const payload: AttemptPayload = {
      module: 'opening',
      slug: `${repertoire.slug}:punish-ply${position!.ply}`,
      fen: position!.fenBefore,
      side_to_move: position!.sideToMove,
      mode: 'punish',
      result,
      time_spent_ms: timeSpentMs,
      source_ref: repertoire.slug,
      expected_move: position!.expectedUci,
      ...(played ? { played_move: played } : {}),
      ...(outcome ? { outcome } : {}),
    };
    openingLabApi.postAttempt(payload).catch((e) => console.error('[punish] log failed', e));
    onResult(result);
  }

  function handleMove({ from, to }: BoardMoveEvent) {
    if (phase === 'revealed' || !chess) return;
    // F-5 P1 : try/catch contre chess.js v1 throw sur coup illégal.
    let move;
    try {
      move = chess.move({ from, to, promotion: 'q' });
    } catch {
      return;
    }
    if (!move) return;
    const playedUci = `${move.from}${move.to}${move.promotion ?? ''}`;
    if (playedUci === position!.expectedUci) {
      if (!isRetrying) {
        const timeSpentMs = Math.round(performance.now() - startedAtRef.current);
        const outcome: Outcome = attempts > 0 ? 'success_slow' : classifySuccessOutcome({ timeSpentMs });
        logAttempt('success', playedUci, timeSpentMs, outcome);
      }
      setPhase('revealed');
      return;
    }
    chess.undo();
    const next = attempts + 1;
    setAttempts(next);
    if (next >= 2) {
      if (!isRetrying) {
        const timeSpentMs = Math.round(performance.now() - startedAtRef.current);
        setLastFail({ playedMove: playedUci, timeSpentMs, outcomeLogged: false });
      }
      setPhase('revealed');
    }
  }

  function handleUnderstanding(understood: boolean): void {
    if (!lastFail || lastFail.outcomeLogged) return;
    const outcome: FailOutcome = understood ? 'fail_understood' : 'fail_not_understood';
    logAttempt('fail', lastFail.playedMove, lastFail.timeSpentMs, outcome);
    setLastFail({ ...lastFail, outcomeLogged: true });
  }

  /** Cf. DrillRecall.retryPosition — réessai sans changer de piège ni biaiser les KPIs. */
  function retryPosition(): void {
    if (lastFail && !lastFail.outcomeLogged) {
      logAttempt('fail', lastFail.playedMove, lastFail.timeSpentMs, 'fail_not_understood');
      setLastFail({ ...lastFail, outcomeLogged: true });
    }
    startedAtRef.current = performance.now();
    setAttempts(0);
    setPhase('playing');
    setIsRetrying(true);
  }

  function pickNext() {
    // T9.1 garde-fou : log l'attempt avec outcome=null si Léon clique
    // « Piège suivant » sans répondre au prompt d'échec (cf. DrillRecall).
    if (lastFail && !lastFail.outcomeLogged) {
      logAttempt('fail', lastFail.playedMove, lastFail.timeSpentMs, undefined);
    }
    setPosition(pickUnseenTrap(traps, seenRef.current));
  }

  return (
    <div className="flex flex-col gap-4">
      <KidHint tone="violet" title="Pourquoi ce drill ?">
        Quand l'adversaire joue une imprécision typique, il faut savoir la
        sanctionner immédiatement. Tu apprends à réfuter les coups d'erreur
        que tu rencontres en partie réelle.
      </KidHint>
      <header className="flex items-baseline justify-between">
        <p className="text-caption uppercase text-violet-500">Riposte · piège coup {position.ply}</p>
        <p className="text-body-sm text-ink-300">Essai {attempts + 1} / 2</p>
      </header>

      <TurnIndicator
        state={phase === 'playing' ? 'user-turn' : 'idle'}
        userLabel="À toi de réfuter"
        idleLabel="Réfutation révélée"
      />

      <Board
        fen={position.fenBefore}
        orientation={repertoire.color}
        interactive={phase === 'playing'}
        legalMoves={legalMoves}
        turnColor={position.sideToMove}
        onMove={handleMove}
      />

      <p className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-body-sm text-amber-500">
        L'adversaire vient de jouer une imprécision typique. Sanctionne.
      </p>

      {phase === 'revealed' && (
        <section className="rounded-xl border border-ink-700 bg-ink-800 p-4">
          <p className="text-body">
            Réfutation :{' '}
            <strong className="font-mono text-acid-500">{position.expectedSan}</strong>
          </p>
          {position.comment && (
            <p className="mt-2 text-body-sm text-ink-300">{position.comment.replace(/#trap\s*/g, '')}</p>
          )}
          {lastFail && !isRetrying && (
            <UnderstandingPrompt
              done={lastFail.outcomeLogged}
              onAnswer={handleUnderstanding}
            />
          )}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {lastFail && (
              <button
                type="button"
                onClick={retryPosition}
                className="rounded-lg border border-violet-500/60 bg-violet-500/10 px-4 py-2 font-display text-body text-violet-500 transition hover:bg-violet-500/20"
              >
                Réessayer
              </button>
            )}
            <button
              type="button"
              onClick={pickNext}
              className="rounded-lg bg-violet-500 px-4 py-2 font-display text-body text-paper-50 transition hover:opacity-90"
            >
              Piège suivant
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

